import AppTheme from "@toothfairy/shared-ui/AppTheme";
import { AppText } from "@toothfairy/shared-ui";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import workspacesAPI from "../../../API/Workspaces/workspacesAPI";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import Charting from "../../../Components/Charting";
import Constants from "../../../Constants";
import DashboardItem from "./DashboardItem";
import DashboardPlaceholder from "./DashboardPlaceholder";

// Per-user & per-model consumption visibility for the Usage tab. Consumes the
// RequestLogs the Dashboard container already pulls for the billing period
// (shared `logs` prop) — no fetch of its own — then replicates the backend's
// UoI formula exactly (UoI = costUSD * 100, 1 UoI = $0.01, with fixed
// per-method costs where no dynamic cost is tracked).
//
// Understanding the logs: every record carries type (the agent method: chatter,
// analyser, generator, ...), modelType (the model SIZE tier: ultra,
// extraLarge, medium, ...) and userID. Note we only store model SIZE, never
// the model name. Requests made through the OpenAI-compatible API
// (/v1/chat/completions) are logged as type "chatter" with agentID "tfcode"
// — there is no dedicated "tfcode" log type — so per-user attribution relies
// on userID, never on the log type.
const FIXED_METHOD_COSTS = {
  searcher: 0.01,
  tokenizer: 0.001,
  embedder: 0.001,
};

const MODEL_LABELS = {
  ultra: "Ultra",
  extraLarge: "Extra Large",
  large: "Large",
  medium: "Medium",
  small: "Small",
  extraSmall: "Extra Small",
  local: "Local",
  custom: "Custom",
  external: "External",
};

const calculateUoI = (event) => {
  const costUSD =
    event?.totalCostUSD && event.totalCostUSD > 0
      ? event.totalCostUSD
      : FIXED_METHOD_COSTS[event?.type] || 0;
  return costUSD * 100;
};

const ConsumptionByUserView = ({
  billingPeriod,
  selectedDate,
  logs = [],
  isFetchInProgress = false,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { getActiveWorkspace } = useWorkspaces();
  const workspace = getActiveWorkspace();

  const [userNames, setUserNames] = useState({});

  // Resolve user IDs to display names in ONE workspace-scoped call
  // (getUsersWorkspace custom resolver — the same call the Members screen
  // uses, Lambda-authed and workspace-scoped, so it reliably returns every
  // member's username/email). Per-id getUser is auth-restricted and silently
  // nulls out some members, which is why names were missing. Any ID that still
  // can't be resolved (removed members, cross-workspace, system) is merged
  // into a single "Unknown user" row downstream.
  useEffect(() => {
    if (!workspace?.id) return;
    let cancelled = false;
    workspacesAPI
      .getWorkspaceUsers(workspace.id)
      .then((res) => {
        if (cancelled) return;
        const items = res?.data?.getUsersWorkspace || [];
        const map = {};
        items.forEach((u) => {
          if (!u?.id) return;
          map[u.id] = u?.username || u?.user_email;
        });
        setUserNames(map);
      })
      .catch((error) =>
        console.error("[ConsumptionByUser] failed to resolve users:", error),
      );
    return () => {
      cancelled = true;
    };
  }, [workspace?.id]);

  const { userRows, totalUoI, totalRequests } = useMemo(() => {
    const byUser = {};
    let _totalUoI = 0;
    let _totalRequests = 0;

    const UNKNOWN = "__unknown__";
    const API = "__api__";

    const bucketFor = (log) => {
      const uid = log?.userID;
      if (uid && userNames[uid]) return uid;
      if (!uid && log?.agentID === "tfcode") return API;
      return UNKNOWN;
    };

    logs.forEach((log) => {
      const uoi = calculateUoI(log);
      const modelType = log?.modelType || "unspecified";

      _totalUoI += uoi;
      _totalRequests += 1;

      const userKey = bucketFor(log);
      if (!byUser[userKey]) {
        byUser[userKey] = {
          id: userKey,
          uoi: 0,
          requests: 0,
          tokens: 0,
          byModel: {},
        };
      }
      byUser[userKey].uoi += uoi;
      byUser[userKey].requests += 1;
      byUser[userKey].tokens += log?.totalTokens || log?.tokens || 0;
      byUser[userKey].byModel[modelType] =
        (byUser[userKey].byModel[modelType] || 0) + uoi;
    });

    const rows = Object.values(byUser)
      .sort((a, b) => b.uoi - a.uoi)
      .map((u) => ({
        ...u,
        topModel:
          Object.keys(u.byModel).sort(
            (a, b) => u.byModel[b] - u.byModel[a],
          )[0] || null,
      }));

    return {
      userRows: rows,
      totalUoI: _totalUoI,
      totalRequests: _totalRequests,
    };
  }, [logs, userNames]);

  if (!workspace) return null;

  const hasData = totalRequests > 0;

  const userLabel = (row) => {
    if (row.id === "__api__") return t("dashboard.consumptionByUser.apiUser");
    if (row.id === "__unknown__")
      return t("dashboard.consumptionByUser.unknownUser");
    return userNames[row.id] || row.id;
  };

  const horizontalBarOptions = {
    indexAxis: "y",
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: {
        display: true,
        position: "bottom",
        ticks: {
          color: currentTheme?.theme?.font_color,
          font: {
            family: "normalFont",
            size: currentTheme?.theme?.extra_small_font_size,
          },
        },
      },
      y: {
        grid: { display: false },
        ticks: {
          color: currentTheme?.theme?.font_color,
          font: {
            family: "normalFont",
            size: currentTheme?.theme?.extra_small_font_size,
          },
        },
      },
    },
  };

  const tableHeaderStyle = {
    fontWeight: "600",
    fontSize: currentTheme?.theme?.extra_small_font_size,
    color: currentTheme?.theme?.softGrey,
  };
  const tableCellStyle = {
    fontSize: currentTheme?.theme?.extra_small_font_size,
  };

  return (
    <View
      style={{
        gap: 5,
        width: "100%",
        alignItems: "center",
      }}
    >
      <DashboardItem
        isFetchInProgress={isFetchInProgress}
        title={t("dashboard.consumptionByUser.title")}
        isHalfWidth={false}
        additionalTitle={` ${Math.round(
          totalUoI,
        ).toLocaleString()} UoI · ${totalRequests.toLocaleString()} ${t(
          "dashboard.consumptionByUser.requestsUnit",
        )}`}
        additionalTitleWrapperStyle={{ marginTop: 3 }}
      >
        {hasData ? (
          <View
            style={{ width: "100%", paddingHorizontal: 16, paddingBottom: 16 }}
          >
            <Charting.AppBarChart
              maxHeight={Constants.chartMaxHeight}
              options={horizontalBarOptions}
              dataset={{
                labels: userRows.map(userLabel),
                datasets: [
                  {
                    label: t("dashboard.consumptionByUser.uoiColumn"),
                    data: userRows.map((u) => parseFloat(u.uoi.toFixed(2))),
                    borderColor: currentTheme?.theme?.primary,
                    backgroundColor: currentTheme?.theme?.primary,
                    borderRadius: 6,
                  },
                ],
              }}
            />
            <View
              style={{
                flexDirection: "row",
                marginTop: 16,
                paddingBottom: 6,
                borderBottomWidth: 1,
                borderBottomColor: currentTheme?.theme?.border_color,
              }}
            >
              <AppText
                wrapperStyle={{ flex: 2 }}
                fontWeight="600"
                style={tableHeaderStyle}
              >
                {t("dashboard.consumptionByUser.userColumn")}
              </AppText>
              <AppText
                wrapperStyle={{ flex: 1 }}
                fontWeight="600"
                style={tableHeaderStyle}
              >
                {t("dashboard.consumptionByUser.requestsColumn")}
              </AppText>
              <AppText
                wrapperStyle={{ flex: 1 }}
                fontWeight="600"
                style={tableHeaderStyle}
              >
                {t("dashboard.consumptionByUser.tokensColumn")}
              </AppText>
              <AppText
                wrapperStyle={{ flex: 1 }}
                fontWeight="600"
                style={tableHeaderStyle}
              >
                {t("dashboard.consumptionByUser.uoiColumn")}
              </AppText>
              <AppText
                wrapperStyle={{ flex: 1 }}
                fontWeight="600"
                style={tableHeaderStyle}
              >
                {t("dashboard.consumptionByUser.modelColumn")}
              </AppText>
            </View>
            {userRows.map((row) => (
              <View
                key={row.id}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  paddingVertical: 8,
                  borderBottomWidth: 1,
                  borderBottomColor: currentTheme?.theme?.border_color,
                }}
              >
                <AppText wrapperStyle={{ flex: 2 }} style={tableCellStyle}>
                  {userLabel(row)}
                </AppText>
                <AppText wrapperStyle={{ flex: 1 }} style={tableCellStyle}>
                  {row.requests.toLocaleString()}
                </AppText>
                <AppText wrapperStyle={{ flex: 1 }} style={tableCellStyle}>
                  {row.tokens.toLocaleString()}
                </AppText>
                <AppText wrapperStyle={{ flex: 1 }} style={tableCellStyle}>
                  {Math.round(row.uoi).toLocaleString()}
                </AppText>
                <AppText wrapperStyle={{ flex: 1 }} style={tableCellStyle}>
                  {row.topModel
                    ? MODEL_LABELS[row.topModel] || row.topModel
                    : t("dashboard.consumptionByUser.unspecifiedModel")}
                </AppText>
              </View>
            ))}
          </View>
        ) : (
          <DashboardPlaceholder
            mainCaption={t("dashboard.consumptionByUser.noFound")}
            subCaption={t("dashboard.consumptionByUser.noFoundSub")}
          />
        )}
      </DashboardItem>
    </View>
  );
};

export default ConsumptionByUserView;
